// src/pages/Archive/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Input, Empty, Typography, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import './index.css';

const { Title, Text } = Typography;

const Archive = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [monthGroups, setMonthGroups] = useState({});
  const [searchText, setSearchText] = useState('');

  // 格式化月份
  const formatMonth = useCallback((dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    return `${year}年${month.toString().padStart(2, '0')}月`;
  }, []);

  // 按月分组
  const groupPostsByMonth = useCallback((postsList) => {
    const groups = {};
    
    postsList.forEach(post => {
      const monthKey = formatMonth(post.createdAt);
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(post);
    });
    
    // 按月份倒序排序
    const sortedGroups = {};
    Object.keys(groups)
      .sort((a, b) => {
        const dateA = new Date(a.replace(/[年月]/g, '-'));
        const dateB = new Date(b.replace(/[年月]/g, '-'));
        return dateB - dateA;
      })
      .forEach(key => {
        sortedGroups[key] = groups[key];
      });
    
    return sortedGroups;
  }, [formatMonth]);

  // 格式化时间
  const formatTime = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // 加载数据
  useEffect(() => {
    const savedPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
    setPosts(savedPosts);
    setFilteredPosts(savedPosts);
    setMonthGroups(groupPostsByMonth(savedPosts));
  }, [groupPostsByMonth]);

  // 搜索文章
  const handleSearch = () => {
    filterPosts(searchText);
  };

  // 过滤文章
  const filterPosts = useCallback((search) => {
    let result = posts;
    
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(post => 
        post.title.toLowerCase().includes(lowerSearch) ||
        (post.summary && post.summary.toLowerCase().includes(lowerSearch)) ||
        post.content.toLowerCase().includes(lowerSearch)
      );
    }
    
    setFilteredPosts(result);
    setMonthGroups(groupPostsByMonth(result));
  }, [posts, groupPostsByMonth]);

  // 回车搜索
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div 
      className="archive-page"
      style={{
        position: 'relative',
        minHeight: '100vh',
        zIndex: 1,
      }}
    >
      {/* 视频背景 */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -1,
        }}
      >
        <source src={`${process.env.PUBLIC_URL}/mmexport1765268612523.mp4`} type="video/mp4" />
        您的浏览器不支持视频播放。
      </video>
      
      {/* 主要内容 - 在JSX中使用内联样式控制透明度 */}
      <div 
        className="page-content"
        style={{
          width: '100%',
          maxWidth: '95%',
          margin: '0 auto',
          padding: '20px 0 60px',
          minHeight: '100vh',
          backgroundColor: 'rgba(255, 255, 255, 0.7)', // ← 这里控制主背景透明度
          backdropFilter: 'blur(8px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div 
          className="archive-header"
          style={{
            padding: '40px 40px 30px',
            textAlign: 'left',
            marginBottom: '30px',
          }}
        >
          <Title level={1} className="page-title">归档</Title>
          <Text className="page-subtitle">
            共 {posts.length} 篇文章，按月份整理
          </Text>
          
          <div className="header-search">
            <div className="search-input-wrapper">
              <div className="search-icon-wrapper" onClick={handleSearch}>
                <SearchOutlined className="search-icon" />
              </div>
              <Input
                placeholder="搜索归档文章..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleKeyPress}
                className="search-input"
                allowClear
                size="large"
              />
            </div>
          </div>
        </div>
        
        <div 
          className="archive-content"
          style={{
            padding: '0 40px 40px',
            minHeight: '500px',
          }}
        >
          {filteredPosts.length === 0 ? (
            <Empty 
              description={
                <div>
                  <Title level={4}>暂无文章</Title>
                  <Text type="secondary">
                    {searchText ? `未找到包含"${searchText}"的文章` : '还没有发布任何文章'}
                  </Text>
                </div>
              }
            />
          ) : (
            <div className="timeline-container">
              <div className="timeline-header">
                <Text strong>时间线</Text>
              </div>
              
              <div className="timeline-content">
                {Object.keys(monthGroups).map(month => (
                  <div 
                    key={month} 
                    className="month-section"
                    style={{
                      marginBottom: '40px',
                      padding: '25px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)', // ← 月份区域透明度
                      backdropFilter: 'blur(3px)',
                      borderRadius: '8px',
                      border: '1px solid rgba(224, 224, 224, 0.5)',
                    }}
                  >
                    <div className="month-header">
                      <div className="month-title-wrapper">
                        <Title level={3} className="month-title">{month}</Title>
                        <Tag className="month-count">{monthGroups[month].length}篇</Tag>
                      </div>
                    </div>
                    
                    <div className="articles-list">
                      {monthGroups[month].map(post => (
                        <div 
                          key={post.id} 
                          className="article-item"
                          style={{
                            padding: '20px',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            borderBottom: '1px solid rgba(240, 240, 240, 0.7)',
                            paddingBottom: '20px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)', // ← 文章项透明度
                            borderRadius: '6px',
                            marginBottom: '8px',
                          }}
                        >
                          <Link to={`/post/${post.id}`} style={{ textDecoration: 'none' }}>
                            <div className="article-title">{post.title}</div>
                          </Link>
                          <div className="article-meta">
                            <div className="article-date">
                              {formatTime(post.createdAt)}
                            </div>
                            <div className="article-views">
                              阅读: {post.viewCount || 0}
                            </div>
                          </div>
                          {post.tags && post.tags.length > 0 && (
                            <div className="article-tags">
                              {post.tags.map(tag => (
                                <Tag 
                                  key={tag} 
                                  className="article-tag"
                                  style={{
                                    background: 'rgba(245, 245, 245, 0.9)', // ← 标签透明度
                                    border: '1px solid rgba(232, 232, 232, 0.7)',
                                    color: '#555',
                                    fontSize: '12px',
                                    padding: '3px 10px',
                                    borderRadius: '10px',
                                    fontWeight: '500',
                                  }}
                                >
                                  {tag}
                                </Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div 
                  className="month-footer"
                  style={{
                    marginTop: '40px',
                    padding: '20px',
                    background: 'rgba(248, 249, 250, 0.9)', // ← 底部透明度
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px solid rgba(232, 232, 232, 0.7)',
                    fontSize: '14px',
                    color: '#666',
                    fontWeight: '500',
                    backdropFilter: 'blur(3px)',
                  }}
                >
                  <div className="no-more-records">
                    <span>没有更多文章了</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Archive;